import type { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import type { RootState } from '@shared/store/store';

import {
  apiService,
  CallDto,
  CampaignCallListPaginatedControllerCallListPaginatedApiArg,
  CampaignCallListPaginatedDto,
  CampaignDto,
  CampaignListPaginatedControllerGetCampaignListPaginatedApiArg,
  CampaignListPaginatedDto,
} from './apiService';
import { CallPushOutcome, upsertCallInPages } from './realtimeCalls.utils';

// Pages are chained by the cursor the previous page returned; the cache keeps every loaded page,
// so socket pushes patch it with updateQueryData and a refetch reloads all of them.
export type CampaignCallsListArg = Omit<
  CampaignCallListPaginatedControllerCallListPaginatedApiArg,
  'cursor' | 'withTotal'
>;
export type CampaignListArg = Omit<
  CampaignListPaginatedControllerGetCampaignListPaginatedApiArg,
  'cursor'
>;
type CursorPageParam = string | null;
type CampaignListPages = { pages: CampaignListPaginatedDto[] };

const getNextCursor = (lastPage: { meta: { hasMore: boolean; cursor: string | null } }) =>
  lastPage.meta.hasMore ? lastPage.meta.cursor : undefined;

// A push carries the whole campaign; rows that are not in the list are not added
const mergeCampaign = (draft: CampaignListPages, campaign: CampaignDto) => {
  for (const page of draft.pages) {
    const existing = page.data.find((item) => item.id === campaign.id);
    if (!existing) continue;

    Object.assign(existing, campaign, {
      agentsCount: campaign.agentsCount || existing.agentsCount,
    });
  }
};

// For a list sorted by a value only the backend computes, pushes cannot place the calls: reload it,
// at most once per delay however many calls change while a campaign is dialing
const CALLS_RELOAD_DELAY_MS = 5_000;
const scheduledCallsReloads = new Map<string, ReturnType<typeof setTimeout>>();

const scheduleCallsReload = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  campaignId: string,
) => {
  if (scheduledCallsReloads.has(campaignId)) return;

  scheduledCallsReloads.set(
    campaignId,
    setTimeout(() => {
      scheduledCallsReloads.delete(campaignId);
      dispatch(realtimeListsApi.util.invalidateTags([{ type: 'CampaignCalls', id: campaignId }]));
    }, CALLS_RELOAD_DELAY_MS),
  );
};

export const realtimeListsApi = apiService.injectEndpoints({
  endpoints: (build) => ({
    campaignCallsPaginated: build.infiniteQuery<
      CampaignCallListPaginatedDto,
      CampaignCallsListArg,
      CursorPageParam
    >({
      infiniteQueryOptions: { initialPageParam: null, getNextPageParam: getNextCursor },
      query: ({ queryArg: { id, ...filters }, pageParam }) => ({
        url: `/v2/campaigns/${id}/calls/paginated`,
        // The total is only counted for the first page
        params: pageParam ? { ...filters, cursor: pageParam } : { ...filters, withTotal: true },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'CampaignCalls', id: arg.id }],
    }),
    campaignsPaginated: build.infiniteQuery<
      CampaignListPaginatedDto,
      CampaignListArg,
      CursorPageParam
    >({
      infiniteQueryOptions: { initialPageParam: null, getNextPageParam: getNextCursor },
      query: ({ queryArg: { withTotal, ...queryArg }, pageParam }) => ({
        url: `/v2/campaigns/paginated`,
        // As for calls, the total is only counted for the first page
        params: pageParam ? { ...queryArg, cursor: pageParam } : { ...queryArg, withTotal },
      }),
      providesTags: ['CampaignList'],
    }),
  }),
  overrideExisting: false,
});

type PushThunk = ThunkAction<void, RootState, unknown, UnknownAction>;

/** Writes a pushed call into the cached list, and schedules a reload when the sort cannot place it */
export const pushCall =
  (arg: CampaignCallsListArg, call: CallDto): PushThunk =>
  (dispatch) => {
    // Set inside the recipe, which does not run when the list is not cached yet
    let outcome = 'applied' as CallPushOutcome;
    dispatch(
      realtimeListsApi.util.updateQueryData('campaignCallsPaginated', arg, (draft) => {
        outcome = upsertCallInPages(draft, call, arg);
      }),
    );
    if (outcome === 'reload') scheduleCallsReload(dispatch, arg.id);
  };

/** Merges a pushed campaign into its cached row */
export const pushCampaign =
  (arg: CampaignListArg, campaign: CampaignDto): PushThunk =>
  (dispatch) => {
    dispatch(
      realtimeListsApi.util.updateQueryData('campaignsPaginated', arg, (draft) =>
        mergeCampaign(draft, campaign),
      ),
    );
  };

export const { useCampaignCallsPaginatedInfiniteQuery, useCampaignsPaginatedInfiniteQuery } =
  realtimeListsApi;
